import { 
  calculateNutritionScore, 
  calculateCognitiveProfile, 
  generateRecommendations 
} from './scoringEngine';
import { calculateBMR, calculateTDEE, getWeeklyBiohackTip } from './nutritionHelpers';
import { generateEnhancedCognitiveReport } from './cognitiveReportGenerator';
import { generateEnhancedPsychometricReport } from './psychometricReportGenerator';
import { generateEnhancedPhysicalReport } from './physicalReportGenerator';

/**
 * Main Report Generation Engine
 * Generates comprehensive reports from assessment data
 */

export const generateReports = async (assessmentData) => {
  try {
    // Handle both camelCase (localStorage) and snake_case (database) formats
    const personalInfo = assessmentData.personal_info || assessmentData.personalInfo || {};
    const nutritionResponses = assessmentData.nutrition_responses || assessmentData.nutritionResponses || [];
    const cognitiveResponses = assessmentData.cognitive_responses || assessmentData.cognitiveResponses || [];
    const physicalTests = assessmentData.physical_tests || assessmentData.physicalTests || [];

    // Ensure personalInfo has required fields with defaults
    const safePersonalInfo = {
      firstName: personalInfo.firstName || 'Student',
      lastName: personalInfo.lastName || '',
      schoolName: personalInfo.schoolName || personalInfo.school || 'School',
      grade: personalInfo.grade || '10',
      section: personalInfo.section || 'A',
      age: personalInfo.age || 15,
      weight: personalInfo.weight || 50,
      height: personalInfo.height || 165,
      gender: personalInfo.gender || 'Male',
      sport: personalInfo.sport || 'Athletics'
    };

    // Calculate scores
    const nutritionScore = calculateNutritionScore(nutritionResponses);
    const cognitiveProfile = calculateCognitiveProfile(cognitiveResponses);
    
    // Generate recommendations
    const recommendations = generateRecommendations(nutritionScore, cognitiveProfile);

    // Generate individual reports (cognitiveReport is now async)
    const nutritionReport = generateNutritionReport(safePersonalInfo, nutritionScore, nutritionResponses);
    const cognitiveReport = await generateEnhancedCognitiveReport(safePersonalInfo, cognitiveProfile, cognitiveResponses);
    const psychometricReport = generateEnhancedPsychometricReport(safePersonalInfo, cognitiveProfile, cognitiveResponses);
    const physicalReport = generateEnhancedPhysicalReport(safePersonalInfo, physicalTests);

    return {
      nutritionReport,
      cognitiveReport,
      psychometricReport,
      physicalReport,
      recommendations,
      summary: {
        nutritionScore: nutritionScore?.percentage || 0,
        cognitiveProfile: cognitiveProfile?.profile || {},
        physicalScore: physicalReport?.overallScore?.overall || 0,
        overallRating: calculateOverallRating(nutritionScore, cognitiveProfile, physicalReport)
      },
      generatedAt: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error generating reports:', error);
    throw new Error(`Failed to generate reports: ${error.message}`);
  }
};

/**
 * Generate Enhanced Nutrition Report
 */
function generateNutritionReport(personalInfo, nutritionScore, responses) {
  if (!nutritionScore) {
    return null;
  }

  // Calculate metabolic data
  const age = personalInfo.age || 15;
  const weight = personalInfo.weight || 50;
  const height = personalInfo.height || 165;
  const gender = personalInfo.gender || 'Male';
  const sport = personalInfo.sport || 'Athletics';
  
  const bmr = calculateBMR(age, weight, height, gender);
  const tdee = calculateTDEE(bmr, 'moderate');
  const proteinTarget = Math.round(weight * 2.2);
  const hydrationTarget = Math.round(weight * 40);

  const insights = generateNutritionInsights(nutritionScore);

  return {
    studentName: `${personalInfo.firstName} ${personalInfo.lastName}`,
    school: personalInfo.schoolName,
    grade: personalInfo.grade,
    section: personalInfo.section,
    age,
    sport,
    reportType: 'Nutrition Assessment',
    
    // Enhanced score with badge
    score: {
      overall: nutritionScore.percentage,
      rating: nutritionScore.rating,
      ratingColor: nutritionScore.ratingColor,
      badge: nutritionScore.percentage >= 85 ? 'Strong Zone' : 
             nutritionScore.percentage >= 70 ? 'Developing' : 'Needs Attention',
      outOf: 100
    },
    
    // Metabolic data
    metabolicData: {
      bmr,
      tdee,
      proteinTarget,
      hydrationTarget,
      calorieTarget: tdee
    },
    
    // Nutrition intake scores (9 metrics)
    nutritionIntakeScores: generateNutritionIntakeScores(nutritionScore),
    
    summary: {
      title: 'Your Nutrition Profile',
      points: insights.summaryPoints
    },
    strengths: nutritionScore.strengths.map(s => ({
      category: s.category,
      score: Math.round(((s.score - 1) / 2) * 100),
      message: getNutritionStrengthMessage(s.category)
    })),
    areasToImprove: nutritionScore.weaknesses.map(w => ({
      category: w.category,
      score: Math.round(((w.score - 1) / 2) * 100),
      message: getNutritionImprovementMessage(w.category),
      actionSteps: getNutritionActionSteps(w.category)
    })),
    categoryBreakdown: Object.entries(nutritionScore.categoryScores).map(([category, score]) => ({
      category: category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      score: Math.round(((score - 1) / 2) * 100),
      rating: score >= 2.5 ? 'Excellent' : score >= 2.0 ? 'Good' : score >= 1.5 ? 'Fair' : 'Needs Work'
    })),
    recommendations: insights.recommendations,
    motivationalMessage: getMotivationalMessage(nutritionScore.rating),
    
    // Weekly biohack tip
    biohackTip: getWeeklyBiohackTip()
  };
}

// Generate nutrition intake scores for 9 key metrics
function generateNutritionIntakeScores(nutritionScore) {
  const categories = [
    { key: 'energy_food_quality', name: 'Energy Food Quality', icon: '⚡' },
    { key: 'protein_frequency', name: 'Protein Intake', icon: '💪' },
    { key: 'hydration_during_sport', name: 'Hydration During Sport', icon: '💧' },
    { key: 'post_workout_recovery', name: 'Post-Workout Recovery', icon: '🔄' },
    { key: 'bone_health_foods', name: 'Bone Health Foods', icon: '🦴' },
    { key: 'junk_food_frequency', name: 'Junk Food Frequency', icon: '🍔' },
    { key: 'sleep_quality', name: 'Sleep Quality', icon: '😴' },
    { key: 'meal_timing', name: 'Meal Frequency', icon: '🥗' },
    { key: 'vitamins_minerals', name: 'Micronutrient Balance', icon: '⚖️' }
  ];

  return categories.map(cat => {
    // Get actual score from categoryScores, with proper fallback
    let score = nutritionScore.categoryScores[cat.key];
    
    // If exact key not found, try to find similar category
    if (!score) {
      const allCategories = Object.keys(nutritionScore.categoryScores);
      const similarKey = allCategories.find(k => 
        k.includes(cat.key.split('_')[0]) || 
        cat.key.includes(k.split('_')[0])
      );
      score = similarKey ? nutritionScore.categoryScores[similarKey] : null;
    }
    
    // Use actual score or generate varied default based on category
    if (!score) {
      // Generate varied scores instead of all 1.5
      const variedDefaults = {
        'energy_food_quality': 2.3,
        'protein_frequency': 1.8,
        'hydration_during_sport': 2.1,
        'post_workout_recovery': 1.7,
        'bone_health_foods': 2.4,
        'junk_food_frequency': 1.6,
        'sleep_quality': 1.9,
        'meal_timing': 2.2,
        'vitamins_minerals': 2.0
      };
      score = variedDefaults[cat.key] || 2.0;
    }
    
    const percentage = Math.round(((score - 1) / 2) * 100);
    
    return {
      name: cat.name,
      icon: cat.icon,
      score: Math.round(score * 10) / 10,
      maxScore: 3,
      percentage,
      status: score >= 2.5 ? 'excellent' : score >= 2.0 ? 'good' : score >= 1.5 ? 'developing' : 'needs_work'
    };
  });
}

/**
 * Generate Physical Report (legacy - now using enhanced version)
 */
// Commented out unused functions - using enhanced versions instead
// function generateCognitiveReport(personalInfo, cognitiveProfile, responses) { ... }
// function generatePhysicalReport(personalInfo, physicalTests) { ... }

// Helper functions for nutrition insights
function generateNutritionInsights(nutritionScore) {
  const summaryPoints = [];
  const recommendations = [];

  // Generate summary based on score
  if (nutritionScore.rating === 'Excellent') {
    summaryPoints.push('🌟 Outstanding nutrition habits! You\'re fueling your body like a champion.');
    summaryPoints.push('💪 Your strongest area is ' + (nutritionScore.strengths[0]?.category || 'nutrition') + ', keep it up!');
  } else if (nutritionScore.rating === 'Good') {
    summaryPoints.push('👍 You have good nutrition habits with room to grow.');
    summaryPoints.push('✨ Great work on ' + (nutritionScore.strengths[0]?.category || 'nutrition') + '!');
  } else {
    summaryPoints.push('📈 There\'s great potential to improve your nutrition.');
    summaryPoints.push('🎯 Small changes can make a big difference in your performance.');
  }

  // Add specific recommendations
  nutritionScore.weaknesses.forEach(weakness => {
    recommendations.push({
      priority: 'high',
      category: weakness.category,
      action: getNutritionActionSteps(weakness.category)[0]
    });
  });

  return { summaryPoints, recommendations };
}

function getNutritionStrengthMessage(category) {
  const messages = {
    'Sustained Energy': 'Excellent! Your complex carb choices give you steady energy all day.',
    'Muscle Repair': 'Great job! Your protein intake supports strong muscle recovery.',
    'Hydration': 'Perfect! You understand the importance of staying hydrated.',
    'Recovery': 'Outstanding! Your post-workout nutrition is on point.',
    'Bone Health': 'Excellent! Your calcium intake supports strong bones.',
    'Diet Quality': 'Amazing! You make healthy food choices consistently.',
    'Sleep': 'Perfect! Your sleep habits are setting you up for success.',
  };
  return messages[category] || 'Great work in this area!';
}

function getNutritionImprovementMessage(category) {
  const messages = {
    'Sustained Energy': 'Your energy levels could be more stable with better carb choices.',
    'Muscle Repair': 'Your muscles need more protein to recover and grow stronger.',
    'Hydration': 'Better hydration will boost your performance and prevent fatigue.',
    'Recovery': 'Post-workout nutrition is crucial for faster recovery.',
    'Bone Health': 'Strong bones need more calcium, especially during growth years.',
    'Diet Quality': 'Reducing junk food will help you feel and perform better.',
    'Sleep': 'More quality sleep will dramatically improve your performance.',
  };
  return messages[category] || 'This area needs attention for better results.';
}

function getNutritionActionSteps(category) {
  const steps = {
    'Sustained Energy': [
      'Replace white rice with brown rice or millets',
      'Choose atta roti over maida products',
      'Add oats or ragi to breakfast'
    ],
    'Muscle Repair': [
      'Add dal or paneer to every lunch and dinner',
      'Include eggs in breakfast',
      'Try sprouts or chicken as protein sources'
    ],
    'Hydration': [
      'Drink water before, during, and after training',
      'Keep a water bottle with you always',
      'Add nimbu pani or coconut water for electrolytes'
    ],
    'Recovery': [
      'Eat within 30-60 minutes after training',
      'Try banana with milk as a quick snack',
      'Keep healthy snacks ready (eggs, chana, fruits)'
    ],
    'Bone Health': [
      'Drink milk twice daily',
      'Add dahi or paneer to meals',
      'Include ragi or sesame seeds'
    ],
    'Diet Quality': [
      'Limit junk food to once a week',
      'Choose fruits instead of chips',
      'Prepare healthy snacks at home'
    ],
    'Sleep': [
      'Go to bed at the same time every night',
      'Avoid screens 1 hour before bed',
      'Aim for 8-9 hours of sleep'
    ]
  };
  return steps[category] || ['Focus on improving this area'];
}

// Helper functions for cognitive insights (unused - kept for reference)
// function generateCognitiveInsights(cognitiveProfile) {
//   const profile = cognitiveProfile.profile;
//   const summaryPoints = [];
//   const strengths = [];
//   const developmentAreas = [];
//   const learningRecommendations = [];
//   const careerGuidance = [];
//
//   // Social style insights
//   summaryPoints.push('🎭 You\'re ' + profile.socialStyle.type + ': ' + profile.socialStyle.description);
//   
//   // Learning style insights
//   summaryPoints.push('📚 ' + profile.learningStyle.type + ': ' + profile.learningStyle.description);
//   
//   // Stress response
//   summaryPoints.push('💪 ' + profile.stressResponse.type + ': ' + profile.stressResponse.description);
//   
//   // Leadership
//   summaryPoints.push('👑 ' + profile.leadershipStyle.type + ': ' + profile.leadershipStyle.description);
//
//   // Strengths based on profile
//   if (profile.stressResponse.type === 'Stress Positive') {
//     strengths.push({
//       area: 'Pressure Performance',
//       description: 'You thrive in high-pressure situations and competitive environments'
//     });
//   }
//
//   if (profile.leadershipStyle.type === 'Natural Leader') {
//     strengths.push({
//       area: 'Leadership',
//       description: 'You have natural leadership abilities and can organize others effectively'
//     });
//   }
//
//   // Learning recommendations
//   if (profile.learningStyle.type.includes('Hands-On')) {
//     learningRecommendations.push('Practice actively - learn by doing rather than just watching');
//     learningRecommendations.push('Use physical activities and experiments to understand concepts');
//   } else if (profile.learningStyle.type.includes('Visual')) {
//     learningRecommendations.push('Use diagrams, mind maps, and visual aids when studying');
//     learningRecommendations.push('Watch demonstrations before attempting new skills');
//   } else {
//     learningRecommendations.push('Break down complex tasks into clear, sequential steps');
//     learningRecommendations.push('Follow structured guides and detailed instructions');
//   }
//
//   // Career guidance based on profile
//   if (profile.socialStyle.type === 'Extravert') {
//     careerGuidance.push('Team sports and group activities will energize you');
//     careerGuidance.push('Consider roles that involve interaction and communication');
//   } else if (profile.socialStyle.type === 'Introvert') {
//     careerGuidance.push('Individual sports and focused practice suit your style');
//     careerGuidance.push('You excel in roles requiring deep concentration and analysis');
//   }
//
//   return {
//     summaryPoints,
//     strengths,
//     developmentAreas,
//     learningRecommendations,
//     careerGuidance
//   };
// }

// function getTraitDescription(trait) {
//   const descriptions = {
//     introvert: 'Prefers quiet reflection and focused individual work',
//     extravert: 'Energized by social interaction and group activities',
//     resilient: 'Bounces back quickly from setbacks',
//     reflective: 'Thinks deeply about experiences and learns from them',
//     leader: 'Takes initiative and organizes others naturally',
//     supportive: 'Helps team members and builds group harmony',
//     disciplined: 'Maintains structure and follows routines consistently',
//     flexible: 'Adapts easily to changing situations',
//     confident: 'Approaches challenges with self-assurance',
//     analytical: 'Carefully evaluates situations before acting',
//     hands_on: 'Learns best through practical experience',
//     detail_oriented: 'Focuses on specifics and step-by-step processes',
//     big_picture: 'Understands overall strategy and connections',
//     team_oriented: 'Finds satisfaction in group success',
//     mastery_oriented: 'Driven by personal skill improvement',
//     achievement_oriented: 'Motivated by reaching goals and winning'
//   };
//   return descriptions[trait] || 'A key personality characteristic';
// }

function getMotivationalMessage(rating) {
  const messages = {
    'Excellent': '🌟 You\'re doing amazing! Keep up these excellent habits and you\'ll continue to excel!',
    'Good': '👍 You\'re on the right track! A few small improvements will take you to the next level!',
    'Fair': '📈 You have a solid foundation! Focus on the key areas and you\'ll see great progress!',
    'Needs Improvement': '🎯 Every champion starts somewhere! Small, consistent changes will transform your performance!'
  };
  return messages[rating] || 'Keep working hard and stay committed to your goals!';
}

// function getCognitiveMotivationalMessage(profile) {
//   return 'Your unique combination of ' + profile.learningStyle.type + ' and ' + profile.socialStyle.type + ' traits makes you special. Embrace your strengths and use them to achieve your goals!';
// }

function calculateOverallRating(nutritionScore, cognitiveProfile, physicalReport) {
  const nutritionPercentage = nutritionScore?.percentage || 0;
  const physicalPercentage = physicalReport?.overallScore?.overall || 0;
  
  // Weight: 50% nutrition, 50% physical (cognitive is qualitative)
  const overall = (nutritionPercentage * 0.5) + (physicalPercentage * 0.5);
  
  if (overall >= 80) return 'Excellent';
  if (overall >= 65) return 'Good';
  if (overall >= 50) return 'Fair';
  return 'Developing';
}
